'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Loading from '@/app/loading';
import { EAdBannerSize } from '@/components/AdBanner/AdBanner.types';
import { BackHeading } from '@/components/BackHeading';
import { useHeader } from '@/components/Header/Header.context';
import { useCustomAuth } from '@/contexts/AuthContext';
import { useComscorePageview } from '@/hooks';
import { getParams } from '@/utils/getParams';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { useFeedContext } from './Feed.Context';
import styles from './Feed.module.scss';

import { AdSkeleton } from '@/components/Skeletons/AdSkeleton';

// Lazy load AdBanner to reduce TBT (Total Blocking Time)
const AdBanner = dynamic(
  () => import('@/components/AdBanner/AdBanner').then((mod) => mod.AdBanner),
  {
    ssr: false,
    loading: () => <AdSkeleton />,
  },
);

const DownloadAppModal = dynamic(
  () =>
    import('@/components/DownloadAppModal').then((mod) => mod.DownloadAppModal),
  { ssr: false },
);
const LoginModal = dynamic(
  () => import('@/components/LoginModal').then((mod) => mod.LoginModal),
  { ssr: false },
);
const EmptyResults = dynamic(
  () => import('@/components/EmptyResults').then((mod) => mod.EmptyResults),
  { ssr: false },
);
const CommentsSection = dynamic(
  () =>
    import('./components/CommentsSection').then((mod) => mod.CommentsSection),
  { ssr: false },
);
const Details = dynamic(
  () => import('./components/Details').then((mod) => mod.Details),
  {
    ssr: true, // Enable SSR for immediate text rendering
  },
);
const VideoSlider = dynamic(
  () => import('./components/VideoSlider').then((mod) => mod.VideoSlider),
  {
    ssr: false,
    loading: () => null,
  },
);

import { IItem } from '@/types/feed.types';

interface IFeedContainer {
  itemId: string;
  playlistId?: string;
  searchString?: string;
  collectionId?: string;
  initialItem?: IItem;
  isMobile: boolean;
}

const FeedContent: React.FC<IFeedContainer> = ({
  itemId,
  playlistId,
  searchString,
  collectionId,
  isMobile,
  initialItem,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    itemList,
    hasNextUrl,
    selectedIndex,
    sectionTitle,
    fetchNextPage,
    hasNextPage,
    isFetching,
    error,
    isError,
  } = useFeedContext();

  const currentItem = useMemo(
    () => itemList[currentIndex],
    [itemList, currentIndex],
  );

  useComscorePageview(currentItem?.id);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [visibleCommentsOnMobile, setVisibleCommentsOnMobile] =
    useState<boolean>(false);
  const [isDownloadAppOpen, setIsDownloadAppOpen] = useState<boolean>(false);

  const { isAuthenticated } = useCustomAuth();
  const { setTitle } = useHeader();

  useEffect(() => {
    setTitle(sectionTitle);
    return () => setTitle('');
  }, [sectionTitle, setTitle]);

  useEffect(() => {
    setCurrentIndex(selectedIndex);
  }, [selectedIndex]);

  const handleFirstImageLoaded = useCallback(() => {
    // Ensure skeleton is hidden (backup in case mount effect didn't fire)
    const serverSkeleton = document.getElementById('server-feed-skeleton');
    if (serverSkeleton && serverSkeleton.style.display !== 'none') {
      serverSkeleton.style.transition = 'opacity 0.3s ease-out';
      serverSkeleton.style.opacity = '0';
      setTimeout(() => {
        serverSkeleton.style.display = 'none';
      }, 300);
    }
  }, []);

  const openModal = useCallback(() => {
    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
    } else {
      setIsDownloadAppOpen(true);
    }
  }, [isAuthenticated]);

  const onSlideChange = useCallback(
    ({ activeIndex }: { activeIndex: number }) => {
      const { id, sanitizedTitle } = itemList[activeIndex];

      const params = getParams({
        ...(playlistId ? { c: playlistId } : {}),
        ...(searchString ? { q: searchString } : {}),
        ...(collectionId ? { f: collectionId } : {}),
      });

      const nextId =
        sanitizedTitle && sanitizedTitle !== '' ? sanitizedTitle : id;

      const nextUrl = `/feed/${nextId}?${params.toString()}`;

      window.history.replaceState(
        { ...window.history.state, as: nextUrl, url: nextUrl },
        '',
        nextUrl,
      );

      sendGTMEvent({
        event: 'web_page_view',
        page_path: nextUrl,
        page_title: sectionTitle,
        playlistId: playlistId ?? '',
        searchString: searchString ?? '',
        collectionId: collectionId ?? '',
      });

      setCurrentIndex(activeIndex);
    },
    [itemList, searchString, sectionTitle, playlistId, collectionId],
  );

  const onReachEnd = useCallback(() => {
    if (hasNextPage && hasNextUrl) fetchNextPage?.();
  }, [hasNextPage, hasNextUrl, fetchNextPage]);

  const itemExists = useMemo(
    () =>
      itemList?.some(
        (item) => item?.id === itemId || item?.sanitizedTitle === itemId,
      ),
    [itemList, itemId],
  );

  useEffect(() => {
    if (isError && error?.message === 'PLAYLIST_NOT_FOUND') {
      router.push(pathname);
    }
  }, [isError, error?.message, pathname, router]);

  if (!itemExists && error?.message !== 'PLAYLIST_NOT_FOUND' && !initialItem) {
    return (
      <EmptyResults
        iconProps={{ icon: faExclamationTriangle }}
        title="Contenido no disponible"
        body="El video que buscas no está disponible en este momento. Intenta más tarde."
      />
    );
  }

  return (
    <>
      <article className={styles.article}>
        <div className={styles.layout}>
          {/* Don't render AdBanner on mobile to reduce TBT */}
          {!isMobile && (
            <aside className={styles.adBanner}>
              <AdBanner size={EAdBannerSize.Tall} hideAtHeight={550} />
            </aside>
          )}

          <section className={styles.videoContainer}>
            <header className={styles.heading}>
              <BackHeading>{sectionTitle}</BackHeading>
            </header>
            {/* Static poster is now rendered in FeedPage (Server Component) */}
            <VideoSlider
              isFetching={isFetching ?? false}
              items={itemList}
              currentIndex={currentIndex}
              onSlideChange={onSlideChange}
              onReachEnd={onReachEnd}
              openModal={openModal}
              onCommentClick={() => setVisibleCommentsOnMobile((prev) => !prev)}
              hasNextUrl={hasNextUrl}
              onFirstImageLoaded={handleFirstImageLoaded}
              onFirstVideoReady={handleFirstImageLoaded}
            />
          </section>

          <aside className={styles.commentsContainer}>
            <section aria-label="Comentarios del video">
              <CommentsSection
                itemId={currentItem?.id}
                commentsCount={currentItem?.commentsCount}
                visibleCommentsOnMobile={visibleCommentsOnMobile}
                setVisibleCommentsOnMobile={setVisibleCommentsOnMobile}
                openRestrictedModal={openModal}
              />
            </section>
          </aside>

          {/* Don't render AdBanners on mobile to reduce TBT */}
          {!isMobile && (
            <aside className={styles.adBanner}>
              <AdBanner
                size={EAdBannerSize.Square}
                hideAtWidth={640}
                className={styles.customBannerContainer}
                instanceId="feed_square_top"
              />
              <section aria-label="Detalles del video" className="hideMobile">
                <Details item={currentItem} />
              </section>
              <AdBanner
                size={EAdBannerSize.Square}
                hideAtWidth={640}
                hideAtHeight={650}
                className={styles.customBannerContainer}
                instanceId="feed_square_bottom"
              />
            </aside>
          )}
        </div>
      </article>

      <LoginModal
        isOpen={isLoginModalOpen}
        setIsOpen={setIsLoginModalOpen}
        urlBeforeLogin={`${pathname}${
          searchParams?.toString() ? `?${searchParams.toString()}` : ''
        }`}
      />

      <DownloadAppModal
        isOpen={isDownloadAppOpen}
        setIsOpen={setIsDownloadAppOpen}
      />
    </>
  );
};

export default function FeedPageWrapper(props: IFeedContainer) {
  return (
    <Suspense fallback={<Loading />}>
      <FeedContent {...props} />
    </Suspense>
  );
}
